import { AxiosHelper } from '../helpers/axiosHelper';
import { StudentType } from '../helpers/definitions';
import { useState } from 'react';
import { DBStudent } from '../../server/definitions';

const useStudents = () => {
  const [students, setStudents] = useState<StudentType[]>([]);
  const getAllStudents = async (): Promise<StudentType[]> => {
    try {
      const res = await AxiosHelper.getWithAxios('/students');
      const typedStudents = res.data.students as unknown as StudentType[];
      setStudents(typedStudents);
      return typedStudents;
    } catch (e) {
      alert('Error getting student list. Please try again later');
      throw e;
    }
  };

  const addEmptyStudent = () => {
    setStudents([
      {
        firstName: '',
        lastName: '',
        primaryParentPhone: '',
        secondaryParentPhone: '',
        barcode: '',
        checkInTime: null,
        sessionTime: 30,
        sendText: true,
      },
      ...students,
    ]);
  };

  const removeNthStudent = (i: number) => {
    const studentsCopy = [...students.slice(0, i), ...students.slice(i + 1)];
    setStudents(studentsCopy);
  };

  const saveStudent = async ({
    firstName,
    lastName,
    primaryParentPhone,
    secondaryParentPhone,
    barcode,
    sessionTime,
    sendText,
  }: StudentType): Promise<boolean> => {
    try {
      if (secondaryParentPhone === '') {
        secondaryParentPhone = null;
      }
      await AxiosHelper.patchWithAxios(`/student/${barcode}`, {
        first_name: firstName,
        last_name: lastName,
        primary_parent_phone: primaryParentPhone,
        secondary_parent_phone: secondaryParentPhone,
        session_time: sessionTime,
        send_text: sendText,
      });
      const studentsCopy = [...students];
      const index = studentsCopy.findIndex((student) => student.barcode === barcode);
      studentsCopy[index] = {
        barcode,
        firstName,
        lastName,
        primaryParentPhone,
        secondaryParentPhone,
        checkInTime: studentsCopy[index].checkInTime,
        sessionTime,
        sendText,
      };
      setStudents(studentsCopy);
      return true;
    } catch (e) {
      alert('Error saving student. Please try again later');
      return false;
    }
  };

  const createStudent = async ({
    firstName,
    lastName,
    primaryParentPhone,
    secondaryParentPhone,
    barcode,
    sessionTime,
    sendText,
  }: StudentType): Promise<boolean> => {
    try {
      if (secondaryParentPhone === '') {
        secondaryParentPhone = null;
      }
      await AxiosHelper.postWithAxios('/student', {
        first_name: firstName,
        last_name: lastName,
        primary_parent_phone: primaryParentPhone,
        secondary_parent_phone: secondaryParentPhone,
        barcode,
        session_time: sessionTime,
        send_text: sendText,
      });
      const studentsCopy = [...students];
      const index = studentsCopy.findIndex((student) => student.barcode === '');
      studentsCopy[index] = {
        barcode,
        firstName,
        lastName,
        primaryParentPhone,
        secondaryParentPhone,
        checkInTime: null,
        sessionTime,
        sendText,
      };
      setStudents(studentsCopy);
      return true;
    } catch (e) {
      alert('Error creating student. Please try again later');
      return false;
    }
  };

  const createStudents = async (students: StudentType[]): Promise<boolean> => {
    students.forEach((student) => {
      if (student.secondaryParentPhone === '') {
        student.secondaryParentPhone = null;
      }
    });
    try {
      const body = {
        students: students.map((student) => ({
          barcode: student.barcode,
          first_name: student.firstName,
          last_name: student.lastName,
          primary_parent_phone: student.primaryParentPhone,
          secondary_parent_phone: student.secondaryParentPhone,
          session_time: student.sessionTime,
          send_text: student.sendText,
        })),
      };
      await AxiosHelper.postWithAxios('/students', body);
      return true;
    } catch (e) {
      alert('Error creating students. Please try again later');
      return false;
    }
  };

  const deleteStudent = async (barcode: string): Promise<boolean> => {
    try {
      await AxiosHelper.deleteWithAxios(`/student/${barcode}`);
      const index = students.findIndex((student) => student.barcode === barcode);
      removeNthStudent(index);
      return true;
    } catch (e) {
      alert('Error deleting student. Please try again later');
      console.error(e);
      return false;
    }
  };

  const checkInStudent = async (barcode: string): Promise<boolean> => {
    try {
      const res = await AxiosHelper.postWithAxios(`/check_in/${barcode}`);
      const studentsCopy = [...students];
      const index = studentsCopy.findIndex((student) => student.barcode === barcode);
      studentsCopy[index].checkInTime = res.data.timestamp;
      setStudents(studentsCopy);
      return true;
    } catch (e) {
      alert('Error checking in student. Please try again later');
      console.error(e);
      return false;
    }
  };

  const scanStudent = async (barcode: string): Promise<DBStudent | null> => {
    try {
      const res = await AxiosHelper.postWithAxios(`/scan/${barcode}`);
      return res.data;
    } catch (e) {
      alert('Error scanning student. Please try again');
      console.error(e);
      return null;
    }
  };

  const checkOutStudent = async (barcode: string): Promise<boolean> => {
    try {
      await AxiosHelper.postWithAxios(`/check_out/${barcode}`);
      const studentsCopy = [...students];
      const index = studentsCopy.findIndex((student) => student.barcode === barcode);
      studentsCopy[index].checkInTime = null;
      setStudents(studentsCopy);
      return true;
    } catch (e) {
      alert('Error checking in student. Please try again later');
      console.error(e);
      return false;
    }
  };
  return {
    students,
    getAllStudents,
    createStudent,
    addEmptyStudent,
    removeNthStudent,
    saveStudent,
    deleteStudent,
    checkInStudent,
    checkOutStudent,
    scanStudent,
    createStudents,
  };
};

export default useStudents;
